import { useCallback, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopNav } from './TopNav';
import { MOBILE_QUERY, useMediaQuery } from './useMediaQuery';

const COLLAPSED_KEY = 'barahpay.admin.sidebarCollapsed';

function initialCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * The Barah Plus app shell: a tinted canvas, a floating sidebar card inset from the edges, header pills
 * floating over the page, and the screen itself below. On narrow screens the sidebar becomes a drawer.
 */
export function AdminLayout() {
  const location = useLocation();
  const mobile = useMediaQuery(MOBILE_QUERY);
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  // The drawer is opened FOR a screen: navigating anywhere else, or growing the window past the
  // breakpoint, puts it away without an effect having to notice.
  const [drawerFor, setDrawerFor] = useState<string | null>(null);
  const drawerOpen = mobile && drawerFor === location.pathname;

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;

      try {
        localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // The choice just will not survive a reload.
      }

      return next;
    });
  }, []);

  const closeDrawer = useCallback(() => setDrawerFor(null), []);

  const railed = collapsed && !mobile;

  return (
    <div className="bp-app">
      {mobile ? (
        drawerOpen ? (
          <>
            <div className="bp-overlay" onClick={closeDrawer} aria-hidden />
            <aside className="bp-sidebar bp-sidebar--drawer">
              <Sidebar mobile collapsed={false} onToggleCollapsed={toggleCollapsed} onClose={closeDrawer} />
            </aside>
          </>
        ) : null
      ) : (
        <aside className={`bp-sidebar${collapsed ? ' is-collapsed' : ''}`}>
          <Sidebar mobile={false} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} onClose={closeDrawer} />
        </aside>
      )}

      <TopNav mobile={mobile} collapsed={railed} onOpenMenu={() => setDrawerFor(location.pathname)} />

      {/* min-width 0 lets a wide grid scroll inside its own container instead of widening the page. */}
      <main className={`bp-main${railed ? ' is-collapsed' : ''}`}>
        {/* Keyed by route so the entrance replays per screen, as in Barah Plus. CSS, not JS: an
            interrupted keyframe simply snaps to visible instead of stranding a page at opacity 0. */}
        <div key={location.pathname} className="bp-page">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
